import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../../api';
import {
  Layers, Users, Upload, FileSpreadsheet, ChevronRight,
  ArrowLeft, CheckCircle2, AlertCircle, RefreshCw, Download,
  X, Loader2, Save, GraduationCap, Sparkles, Award, Search, Lock
} from 'lucide-react';
import './StageAllocation.css';

const STAGES = ['Basic', 'Pro', 'Enterprise'];

// Helper for student initials
const getInitials = (name) => {
  if (!name) return 'S';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Color palette for student avatars (matches AI Student Reports)
const getAvatarColor = (name) => {
  const colors = [
    { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
    { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
    { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
    { bg: '#fff7ed', text: '#d97706', border: '#fed7aa' },
    { bg: '#fdf2f8', text: '#db2777', border: '#fbcfe8' },
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash += name.charCodeAt(i);
  return colors[Math.abs(hash) % colors.length];
};

const StagePill = ({ stage }) => {
  const palette = stage === 'Enterprise'
    ? { bg: '#fffbeb', border: '#fde68a', color: '#b45309', dot: '#d97706' }
    : stage === 'Pro'
      ? { bg: '#f5f3ff', border: '#ddd6fe', color: '#6d28d9', dot: '#7c3aed' }
      : { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8', dot: '#3b82f6' };

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '2px 9px', borderRadius: '20px', fontSize: '0.75rem',
      fontWeight: '600', background: palette.bg, border: `1px solid ${palette.border}`, color: palette.color
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: palette.dot, display: 'inline-block' }} />
      {stage}
    </span>
  );
};

// Inline stage multi-select checkbox selector for a student row
const StageSelector = ({ studentId, currentStages = ['Basic'], onSave, saving }) => {
  const normCurrent = useMemo(() => {
    if (Array.isArray(currentStages)) return currentStages.length > 0 ? currentStages : ['Basic'];
    if (typeof currentStages === 'string') return currentStages.split(',').map(s => s.trim()).filter(Boolean);
    return ['Basic'];
  }, [currentStages]);

  const [selected, setSelected] = useState(normCurrent);

  // Sync if parent stage changes (e.g. after refresh or Excel upload)
  useEffect(() => {
    setSelected(normCurrent);
  }, [normCurrent]);

  const isDirty = useMemo(() => {
    const curSorted = [...normCurrent].sort().join(',');
    const selSorted = [...selected].sort().join(',');
    return curSorted !== selSorted;
  }, [normCurrent, selected]);

  const toggleStage = (stageName) => {
    setSelected(prev => {
      if (prev.includes(stageName)) {
        // Prevent deselecting everything so student has at least 1 stage
        if (prev.length <= 1) return prev;
        return prev.filter(s => s !== stageName);
      } else {
        return [...prev, stageName];
      }
    });
  };

  return (
    <div className="sa-stage-selector">
      {STAGES.map((s) => {
        const isActive = selected.includes(s);
        const stageClass = `stage-${s.toLowerCase()}`;
        return (
          <label
            key={s}
            className={`sa-stage-radio ${stageClass} ${isActive ? 'active' : ''}`}
            title={isActive ? `Selected (${s})` : `Click to enable ${s}`}
          >
            <input
              type="checkbox"
              name={`stage-${studentId}-${s}`}
              value={s}
              checked={isActive}
              onChange={() => toggleStage(s)}
              style={{ display: 'none' }}
            />
            {s}
          </label>
        );
      })}
      {isDirty && (
        <button
          className="sa-stage-save"
          onClick={() => onSave(studentId, selected)}
          disabled={saving}
          title="Save stages"
        >
          {saving ? <Loader2 size={13} className="sa-spin" /> : <Save size={13} />}
          <span>Save</span>
        </button>
      )}
    </div>
  );
};

const StageAllocation = ({ showToast = () => {}, universityId = null, institutionName = null, readOnly = false }) => {
  const showToastRef = useRef(showToast);
  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  const [internalToast, setInternalToast] = useState(null);

  const triggerToast = useCallback((msg, type = 'info') => {
    if (showToastRef.current && typeof showToastRef.current === 'function') {
      try { showToastRef.current(msg, type); } catch (e) {}
    }
    setInternalToast({ msg, type });
    setTimeout(() => {
      setInternalToast(prev => (prev?.msg === msg ? null : prev));
    }, 4000);
  }, []);

  const [view, setView] = useState('batches');
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Batches view
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [batches, setBatches] = useState([]);
  const [batchSearchTerm, setBatchSearchTerm] = useState('');

  // Batch detail: students list
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [students, setStudents] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all'); // 'all' | 'Basic' | 'Pro' | 'Enterprise'

  // Upload
  const fileInputRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showUploadPanel, setShowUploadPanel] = useState(false);

  // ── Fetch all students, aggregate into batches ──
  const fetchBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const params = {};
      if (universityId) params.university_id = universityId;
      if (institutionName) params.institution_name = institutionName;
      const res = await api.get('/sidebar-interviewer/stage-allocation', { params });
      const studentList = res.data?.students || [];
      const batchMap = {};
      for (const s of studentList) {
        const batch = s.academic_batch || s.graduation_year || 'Unknown Batch';
        if (!batchMap[batch]) batchMap[batch] = { label: batch, count: 0, basic: 0, pro: 0, enterprise: 0 };
        batchMap[batch].count++;
        const sStages = s.allocated_stages || (s.allocated_stage ? s.allocated_stage.split(',').map(x => x.trim()) : ['Basic']);
        if (sStages.includes('Basic')) batchMap[batch].basic++;
        if (sStages.includes('Pro')) batchMap[batch].pro++;
        if (sStages.includes('Enterprise')) batchMap[batch].enterprise++;
      }
      setBatches(Object.values(batchMap).sort((a, b) => a.label.localeCompare(b.label)));
    } catch (err) {
      triggerToast(err.response?.data?.detail || 'Failed to load batches', 'error');
    } finally {
      setLoadingBatches(false);
    }
  }, [universityId, institutionName, triggerToast]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  // Institutional KPI summary across all batches
  const overallMetrics = useMemo(() => {
    return batches.reduce(
      (acc, b) => {
        acc.totalBatches += 1;
        acc.totalStudents += b.count;
        acc.totalBasic += b.basic;
        acc.totalPro += b.pro;
        acc.totalEnterprise += b.enterprise;
        return acc;
      },
      { totalBatches: 0, totalStudents: 0, totalBasic: 0, totalPro: 0, totalEnterprise: 0 }
    );
  }, [batches]);

  // ── Fetch students for a specific batch ──
  const fetchBatchStudents = useCallback(async (batchLabel) => {
    setLoadingStudents(true);
    try {
      const params = {};
      if (batchLabel && batchLabel !== 'Unknown Batch') params.batch = batchLabel;
      if (universityId) params.university_id = universityId;
      if (institutionName) params.institution_name = institutionName;
      const res = await api.get('/sidebar-interviewer/stage-allocation', { params });
      setStudents(res.data?.students || []);
    } catch (err) {
      triggerToast(err.response?.data?.detail || 'Failed to load students', 'error');
    } finally {
      setLoadingStudents(false);
    }
  }, [universityId, institutionName, triggerToast]);

  // Auto-refresh when window or tab regains focus (keeps read-only University portal synced with Admin changes)
  useEffect(() => {
    const handleFocus = () => {
      if (view === 'batches') {
        fetchBatches();
      } else if (selectedBatch?.label) {
        fetchBatchStudents(selectedBatch.label);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [view, selectedBatch, fetchBatches, fetchBatchStudents]);

  const openBatch = (batch) => {
    setSelectedBatch(batch);
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadPanel(false);
    setStudentSearchTerm('');
    setStageFilter('all');
    setView('detail');
    fetchBatchStudents(batch.label);
  };

  const backToBatches = () => {
    setView('batches');
    setSelectedBatch(null);
    setStudents([]);
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadPanel(false);
    setStudentSearchTerm('');
    setStageFilter('all');
    fetchBatches();
  };

  // Batch detail metrics
  const batchDetailMetrics = useMemo(() => {
    let basic = 0;
    let pro = 0;
    let enterprise = 0;
    students.forEach((s) => {
      const sStages = s.allocated_stages || (s.allocated_stage ? s.allocated_stage.split(',').map(x => x.trim()) : ['Basic']);
      if (sStages.includes('Basic')) basic++;
      if (sStages.includes('Pro')) pro++;
      if (sStages.includes('Enterprise')) enterprise++;
    });
    return { total: students.length, basic, pro, enterprise };
  }, [students]);

  // Filtered students in detail view
  const filteredStudents = useMemo(() => {
    const q = studentSearchTerm.toLowerCase().trim();
    return students.filter((s) => {
      const sStages = s.allocated_stages || (s.allocated_stage ? s.allocated_stage.split(',').map(x => x.trim()) : ['Basic']);
      const matchesStage = stageFilter === 'all' || sStages.includes(stageFilter);
      const matchesSearch =
        !q ||
        (s.full_name && s.full_name.toLowerCase().includes(q)) ||
        (s.email && s.email.toLowerCase().includes(q)) ||
        (s.usn && String(s.usn).toLowerCase().includes(q)) ||
        (s.department && s.department.toLowerCase().includes(q)) ||
        (s.branch && s.branch.toLowerCase().includes(q));
      return matchesStage && matchesSearch;
    });
  }, [students, studentSearchTerm, stageFilter]);

  // ── Save individual student stages ──
  const handleSaveStage = async (studentId, newStages) => {
    setSavingId(studentId);
    try {
      await api.put(`/sidebar-interviewer/stage-allocation/student/${studentId}`, { stages: newStages });
      setStudents(prev => prev.map(s =>
        s.id === studentId ? {
          ...s,
          allocated_stage: newStages[0] || 'Basic',
          allocated_stages: newStages
        } : s
      ));
      triggerToast(`Stage(s) updated to ${newStages.join(', ')}.`, 'success');
    } catch (err) {
      triggerToast(err.response?.data?.detail || 'Failed to update stages', 'error');
    } finally {
      setSavingId(null);
    }
  };

  // ── File handlers ──
  const handleFileSelect = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      triggerToast('Please upload an Excel or CSV file (.xlsx, .xls, .csv)', 'error');
      return;
    }
    setUploadFile(f);
    setUploadResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  // ── Upload to backend ──
  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      if (selectedBatch?.label && selectedBatch.label !== 'Unknown Batch') {
        form.append('batch', selectedBatch.label);
      }
      if (universityId) {
        form.append('university_id', universityId);
      }
      if (institutionName) {
        form.append('institution_name', institutionName);
      }
      const res = await api.post('/sidebar-interviewer/stage-allocation/excel-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      if (res.data.errors === 0) {
        triggerToast(`✅ ${res.data.updated} student(s) updated!`, 'success');
      } else {
        triggerToast(`Upload done — ${res.data.updated} updated, ${res.data.errors} issue(s).`, 'warning');
      }
      // Refresh student list to reflect new stages
      fetchBatchStudents(selectedBatch?.label);
    } catch (err) {
      triggerToast(err.response?.data?.detail || 'Upload failed.', 'error');
    } finally {
      setUploading(false);
    }
  };

  // ── Download template ──
  const handleDownloadTemplate = () => {
    const stageToNum = { Basic: '1', Pro: '2', Enterprise: '3' };
    const templateRows = students.length > 0
      ? students.map((s) => {
          const sStages = s.allocated_stages || (s.allocated_stage ? s.allocated_stage.split(',').map(x => x.trim()) : ['Basic']);
          const stageNums = sStages.map(st => stageToNum[st] || st).join(', ');
          return {
            Name: s.full_name || s.email?.split('@')[0] || 'Student',
            Email: s.email || '',
            Stage: stageNums || '1'
          };
        })
      : [
          { Name: 'Alex Rivera', Email: 'alex.rivera@example.com', Stage: '1, 2' },
          { Name: 'John Smith', Email: 'john.smith@example.com', Stage: '2' },
          { Name: 'Jane Doe', Email: 'jane.doe@example.com', Stage: '1, 2, 3' }
        ];

    const worksheet = XLSX.utils.json_to_sheet(templateRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stage Allocation');
    const batchSlug = selectedBatch?.label ? `_${String(selectedBatch.label).replace(/[^a-zA-Z0-9_-]/g, '_')}` : '';
    XLSX.writeFile(workbook, `stage_allocation_template${batchSlug}.xlsx`);
  };

  // ── Export students to Excel (Read-only view) ──
  const handleExportStudents = () => {
    const listToExport = filteredStudents.length > 0 ? filteredStudents : students;
    if (listToExport.length === 0) {
      triggerToast('No students to export in this batch.', 'warning');
      return;
    }
    const exportRows = listToExport.map((s) => {
      const sStages = s.allocated_stages || (s.allocated_stage ? s.allocated_stage.split(',').map(x => x.trim()) : ['Basic']);
      return {
        'Student Name': s.full_name || s.email?.split('@')[0] || 'Student',
        'Email': s.email || '',
        'USN / Roll No': s.usn || '',
        'Department': s.department || s.branch || '',
        'Batch': s.academic_batch || selectedBatch?.label || '',
        'Allocated Stage(s)': sStages.join(', ')
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stage Allocation');
    const batchSlug = selectedBatch?.label ? `_${String(selectedBatch.label).replace(/[^a-zA-Z0-9_-]/g, '_')}` : '';
    XLSX.writeFile(workbook, `ai_interview_tiers${batchSlug}.xlsx`);
    triggerToast(`Exported ${exportRows.length} student tier record(s).`, 'success');
  };

  // ══════════════════════════════════════════
  //  RENDER: Batches list (AI Student Reports style)
  // ══════════════════════════════════════════
  const renderBatches = () => {
    const filteredBatches = batches.filter(b =>
      !batchSearchTerm || b.label.toLowerCase().includes(batchSearchTerm.toLowerCase().trim())
    );

    return (
      <div className="sa-batches-view">
        {/* Header */}
        <div className="sa-header">
          <div>
            <div className="sa-title-row">
              <div className="sa-title-icon">
                <Layers size={22} />
              </div>
              <h2 className="sa-title">AI Interview Tiers</h2>
            </div>
            <p className="sa-subtitle">
              {readOnly
                ? (institutionName
                    ? `View allocated AI interview tiers for ${institutionName}. Allocations are managed by Platform Administrators.`
                    : 'View allocated AI interview tiers across student cohorts. Allocations are managed by Platform Administrators.')
                : (institutionName
                    ? `Manage AI interview tier allocations for ${institutionName}.`
                    : 'Select an academic student batch to manage AI interview tier allocations across cohorts.')}
            </p>
            {readOnly && (
              <div className="sa-readonly-badge">
                <Lock size={12} />
                <span>Read-Only Mode — Tier allocations managed by Platform Administrator</span>
              </div>
            )}
          </div>
          <button className="sa-btn-ghost" onClick={fetchBatches} disabled={loadingBatches}>
            <RefreshCw size={16} className={loadingBatches ? 'sa-spin' : ''} />
            <span>{loadingBatches ? 'Refreshing...' : 'Refresh Batches'}</span>
          </button>
        </div>

        {/* Institutional Summary KPI Cards */}
        <div className="sa-kpi-grid">
          <div className="sa-kpi-card blue">
            <div>
              <div className="sa-kpi-label">Student Batches</div>
              <div className="sa-kpi-value">{overallMetrics.totalBatches}</div>
            </div>
            <div className="sa-kpi-icon blue">
              <Layers size={26} />
            </div>
          </div>

          <div className="sa-kpi-card emerald">
            <div>
              <div className="sa-kpi-label">Enrolled Students</div>
              <div className="sa-kpi-value">{overallMetrics.totalStudents}</div>
            </div>
            <div className="sa-kpi-icon emerald">
              <GraduationCap size={26} />
            </div>
          </div>

          <div className="sa-kpi-card sky">
            <div>
              <div className="sa-kpi-label">Basic</div>
              <div className="sa-kpi-value">{overallMetrics.totalBasic}</div>
              <div className="sa-kpi-sub sky">Stage 1 Allocated</div>
            </div>
            <div className="sa-kpi-icon sky">
              <Users size={26} />
            </div>
          </div>

          <div className="sa-kpi-card purple">
            <div>
              <div className="sa-kpi-label">Pro</div>
              <div className="sa-kpi-value">{overallMetrics.totalPro}</div>
              <div className="sa-kpi-sub purple">Stage 2 Allocated</div>
            </div>
            <div className="sa-kpi-icon purple">
              <Sparkles size={26} />
            </div>
          </div>

          <div className="sa-kpi-card amber">
            <div>
              <div className="sa-kpi-label">Enterprise</div>
              <div className="sa-kpi-value">{overallMetrics.totalEnterprise}</div>
              <div className="sa-kpi-sub amber">Stage 3 Allocated</div>
            </div>
            <div className="sa-kpi-icon amber">
              <Award size={26} />
            </div>
          </div>
        </div>

        {/* Toolbar: Search + Legend */}
        <div className="sa-toolbar">
          {batches.length > 0 && (
            <div className="sa-search-box">
              <Search size={16} className="sa-search-icon" />
              <input
                type="text"
                className="sa-search-input"
                placeholder="Search batches..."
                value={batchSearchTerm}
                onChange={(e) => setBatchSearchTerm(e.target.value)}
              />
              {batchSearchTerm && (
                <button className="sa-search-clear" onClick={() => setBatchSearchTerm('')}>
                  <X size={15} />
                </button>
              )}
            </div>
          )}

          <div className="sa-legend">
            <span className="sa-legend-item"><span className="sa-dot basic" />Basic (Stage 1)</span>
            <span className="sa-legend-item"><span className="sa-dot pro" />Pro (Stage 2)</span>
            <span className="sa-legend-item"><span className="sa-dot enterprise" />Enterprise (Stage 3)</span>
          </div>
        </div>

        {/* Batches Grid */}
        {loadingBatches ? (
          <div className="sa-loading">
            <RefreshCw size={32} className="sa-spin" style={{ color: '#2563eb' }} />
            <span>Loading student batches and interview tier allocations...</span>
          </div>
        ) : filteredBatches.length === 0 ? (
          <div className="sa-empty">
            <Layers size={44} style={{ color: '#94a3b8', opacity: 0.5 }} />
            <h3>No student batches found</h3>
            <p>
              {batchSearchTerm
                ? 'No batches match your search term.'
                : institutionName
                  ? `No enrolled student batches found for ${institutionName}.`
                  : 'Your institution does not have any enrolled student batches yet.'}
            </p>
          </div>
        ) : (
          <div className="sa-batch-grid">
            {filteredBatches.map((b) => {
              const totalAlloc = (b.basic || 0) + (b.pro || 0) + (b.enterprise || 0) || 1;
              const basicPct      = b.basic > 0 ? Math.round((b.basic / totalAlloc) * 100) : 0;
              const proPct        = b.pro > 0 ? Math.round((b.pro / totalAlloc) * 100) : 0;
              const enterprisePct = b.enterprise > 0 ? Math.round((b.enterprise / totalAlloc) * 100) : 0;

              return (
                <div
                  key={b.label}
                  role="button"
                  tabIndex={0}
                  className="sa-batch-card"
                  onClick={() => openBatch(b)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBatch(b); } }}
                >
                  <div>
                    {/* Top row */}
                    <div className="sa-batch-card-top">
                      <div className="sa-batch-card-identity">
                        <div className="sa-batch-icon">
                          <Users size={20} />
                        </div>
                        <div className="sa-batch-info">
                          <div className="sa-batch-label">Batch {b.label}</div>
                          <div className="sa-batch-count">
                            {b.count} student{b.count !== 1 ? 's' : ''} enrolled
                          </div>
                        </div>
                      </div>

                      <div className="sa-batch-arrow-circle">
                        <ChevronRight size={16} />
                      </div>
                    </div>

                    {/* 3-Stage Stat Boxes */}
                    <div className="sa-batch-stats-grid">
                      <div className={`sa-batch-stat-box ${b.basic > 0 ? 'has-basic' : ''}`}>
                        <div className="sa-batch-stat-label">Basic</div>
                        <div className="sa-batch-stat-value">{b.basic}</div>
                      </div>
                      <div className={`sa-batch-stat-box ${b.pro > 0 ? 'has-pro' : ''}`}>
                        <div className="sa-batch-stat-label">Pro</div>
                        <div className="sa-batch-stat-value">{b.pro}</div>
                      </div>
                      <div className={`sa-batch-stat-box ${b.enterprise > 0 ? 'has-enterprise' : ''}`}>
                        <div className="sa-batch-stat-label">Enterprise</div>
                        <div className="sa-batch-stat-value">{b.enterprise}</div>
                      </div>
                    </div>

                    {/* Distribution bar */}
                    <div className="sa-dist-bar">
                      {basicPct > 0 && <div className="sa-dist-seg basic" style={{ width: `${basicPct}%` }} title={`Basic: ${b.basic}`} />}
                      {proPct > 0 && <div className="sa-dist-seg pro" style={{ width: `${proPct}%` }} title={`Pro: ${b.pro}`} />}
                      {enterprisePct > 0 && <div className="sa-dist-seg enterprise" style={{ width: `${enterprisePct}%` }} title={`Enterprise: ${b.enterprise}`} />}
                    </div>
                  </div>

                  {/* Card Action Footer */}
                  <div className="sa-batch-card-footer">
                    <span>{readOnly ? 'View Allocated Tiers' : 'Manage Interview Tiers'}</span>
                    <ChevronRight size={15} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ══════════════════════════════════════════
  //  RENDER: Batch detail — students + upload
  // ══════════════════════════════════════════
  const renderDetail = () => (
    <div className="sa-detail-view">
      {/* Header */}
      <div className="sa-header">
        <div>
          <button className="sa-btn-back" onClick={backToBatches}>
            <ArrowLeft size={15} />
            <span>Back to All Batches</span>
          </button>
          <div className="sa-title-row">
            <div className="sa-title-icon">
              <Layers size={22} />
            </div>
            <h2 className="sa-title">AI Interview Tiers — Batch {selectedBatch?.label}</h2>
          </div>
          <p className="sa-subtitle">
            {readOnly
              ? `Allocated interview tiers for students in Batch ${selectedBatch?.label}. Tier assignments are managed by Platform Administrators.`
              : `Toggle interview tiers for individual students in Batch ${selectedBatch?.label} or bulk allocate via Excel.`}
          </p>
          {readOnly && (
            <div className="sa-readonly-badge">
              <Lock size={12} />
              <span>Read-Only Mode — Tier allocations managed by Platform Administrator</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {readOnly ? (
            <button
              className="sa-btn-ghost"
              onClick={handleExportStudents}
              disabled={students.length === 0}
              title="Export students and tier allocations to Excel"
            >
              <FileSpreadsheet size={16} />
              <span>Export to Excel</span>
            </button>
          ) : (
            <button
              className={`sa-btn-ghost ${showUploadPanel ? 'sa-btn-primary-outline' : ''}`}
              onClick={() => { setShowUploadPanel(v => !v); setUploadFile(null); setUploadResult(null); }}
            >
              <Upload size={16} />
              <span>{showUploadPanel ? 'Hide Bulk Upload' : 'Bulk Upload Excel'}</span>
            </button>
          )}
          <button className="sa-btn-ghost" onClick={() => fetchBatchStudents(selectedBatch?.label)} disabled={loadingStudents}>
            <RefreshCw size={16} className={loadingStudents ? 'sa-spin' : ''} />
            <span>{loadingStudents ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Batch KPI Cards */}
      <div className="sa-kpi-grid">
        <div className="sa-kpi-card emerald">
          <div>
            <div className="sa-kpi-label">Enrolled Students</div>
            <div className="sa-kpi-value">{batchDetailMetrics.total}</div>
          </div>
          <div className="sa-kpi-icon emerald">
            <GraduationCap size={26} />
          </div>
        </div>

        <div className="sa-kpi-card sky">
          <div>
            <div className="sa-kpi-label">Basic</div>
            <div className="sa-kpi-value">{batchDetailMetrics.basic}</div>
            <div className="sa-kpi-sub sky">Stage 1 Allocated</div>
          </div>
          <div className="sa-kpi-icon sky">
            <Users size={26} />
          </div>
        </div>

        <div className="sa-kpi-card purple">
          <div>
            <div className="sa-kpi-label">Pro</div>
            <div className="sa-kpi-value">{batchDetailMetrics.pro}</div>
            <div className="sa-kpi-sub purple">Stage 2 Allocated</div>
          </div>
          <div className="sa-kpi-icon purple">
            <Sparkles size={26} />
          </div>
        </div>

        <div className="sa-kpi-card amber">
          <div>
            <div className="sa-kpi-label">Enterprise</div>
            <div className="sa-kpi-value">{batchDetailMetrics.enterprise}</div>
            <div className="sa-kpi-sub amber">Stage 3 Allocated</div>
          </div>
          <div className="sa-kpi-icon amber">
            <Award size={26} />
          </div>
        </div>
      </div>

      {/* Excel upload panel (collapsible - Admin only) */}
      {!readOnly && showUploadPanel && (
        <div className="sa-upload-panel">
          <div className="sa-upload-panel-header">
            <FileSpreadsheet size={16} color="#2563eb" />
            <span>Bulk Upload via Excel</span>
            <span className="sa-upload-hint">Columns: Name, Email, Stage (1=Basic, 2=Pro, 3=Enterprise — e.g. "1, 2" for multiple)</span>
            <button className="sa-btn-ghost sa-ml-auto" onClick={handleDownloadTemplate} style={{ fontSize: '12px', padding: '6px 12px' }}>
              <Download size={13} /> Download Template
            </button>
          </div>

          {!uploadResult ? (
            <>
              <div
                className={`sa-dropzone compact${dragOver ? ' drag-over' : ''}${uploadFile ? ' has-file' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => !uploadFile && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
                {uploadFile ? (
                  <div className="sa-file-selected">
                    <FileSpreadsheet size={24} color="#2563eb" />
                    <div className="sa-file-info">
                      <span className="sa-file-name">{uploadFile.name}</span>
                      <span className="sa-file-size">{(uploadFile.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <button className="sa-file-remove" onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}>
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload size={22} style={{ color: dragOver ? '#2563eb' : '#94a3b8' }} />
                    <span className="sa-drop-title">{dragOver ? 'Drop file here' : 'Drag & drop an Excel/CSV file or click to browse'}</span>
                  </>
                )}
              </div>
              {uploadFile && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <button className="sa-btn-upload" onClick={handleUpload} disabled={uploading}>
                    {uploading ? <><Loader2 size={14} className="sa-spin" /> Processing…</> : <><Upload size={14} /> Upload & Allocate</>}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="sa-results">
              <div className={`sa-result-summary ${uploadResult.errors === 0 ? 'all-ok' : 'has-errors'}`}>
                <div className="sa-result-stat"><CheckCircle2 size={16} color="#22c55e" /><span><strong>{uploadResult.updated}</strong> updated</span></div>
                {uploadResult.errors > 0 && <div className="sa-result-stat"><AlertCircle size={16} color="#ef4444" /><span><strong>{uploadResult.errors}</strong> issue{uploadResult.errors !== 1 ? 's' : ''}</span></div>}
                <span className="sa-result-msg">{uploadResult.message}</span>
                <button className="sa-btn-ghost" style={{ marginLeft: 'auto', fontSize: '12px', padding: '5px 12px' }} onClick={() => { setUploadFile(null); setUploadResult(null); }}>
                  Upload Another
                </button>
              </div>
              {/* Row-by-row table */}
              <div className="sa-result-table-wrap">
                <table className="sa-result-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Stage(s)</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.results.map((r) => (
                      <tr key={r.row} className={`sa-result-row ${r.status}`}>
                        <td className="sa-result-row-num">#{r.row}</td>
                        <td>{r.name || '—'}</td>
                        <td><code>{r.email || '—'}</code></td>
                        <td>
                          {r.stages ? (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {r.stages.map(st => <StagePill key={st} stage={st} />)}
                            </div>
                          ) : r.stage ? (
                            <StagePill stage={r.stage} />
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          )}
                        </td>
                        <td>
                          <span className={`sa-result-status ${r.status}`}>
                            {r.status === 'success' && <CheckCircle2 size={12} />}
                            {r.status !== 'success' && <AlertCircle size={12} />}
                            {r.message}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search & Stage Filter Toolbar */}
      <div className="sa-detail-toolbar">
        <div className="sa-search-box">
          <Search size={17} className="sa-search-icon" />
          <input
            type="text"
            className="sa-search-input"
            placeholder="Search student name, email, or department..."
            value={studentSearchTerm}
            onChange={(e) => setStudentSearchTerm(e.target.value)}
          />
          {studentSearchTerm && (
            <button className="sa-search-clear" onClick={() => setStudentSearchTerm('')}>
              <X size={15} />
            </button>
          )}
        </div>

        <div className="sa-stage-filter-tabs">
          <button
            className={`sa-stage-filter-tab ${stageFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStageFilter('all')}
          >
            All Students ({batchDetailMetrics.total})
          </button>
          <button
            className={`sa-stage-filter-tab basic ${stageFilter === 'Basic' ? 'active' : ''}`}
            onClick={() => setStageFilter('Basic')}
          >
            Basic ({batchDetailMetrics.basic})
          </button>
          <button
            className={`sa-stage-filter-tab pro ${stageFilter === 'Pro' ? 'active' : ''}`}
            onClick={() => setStageFilter('Pro')}
          >
            Pro ({batchDetailMetrics.pro})
          </button>
          <button
            className={`sa-stage-filter-tab enterprise ${stageFilter === 'Enterprise' ? 'active' : ''}`}
            onClick={() => setStageFilter('Enterprise')}
          >
            Enterprise ({batchDetailMetrics.enterprise})
          </button>
        </div>
      </div>

      {/* Student list */}
      {loadingStudents ? (
        <div className="sa-loading">
          <RefreshCw size={28} className="sa-spin" style={{ color: '#2563eb' }} />
          <span>Loading students in Batch {selectedBatch?.label}…</span>
        </div>
      ) : filteredStudents.length === 0 ? (
        <div className="sa-empty">
          <Users size={40} style={{ color: '#94a3b8', opacity: 0.5 }} />
          <h3>No students found</h3>
          <p>
            {studentSearchTerm || stageFilter !== 'all'
              ? 'No students match your current search or stage filter.'
              : 'No students found in this batch.'}
          </p>
        </div>
      ) : (
        <div className="sa-student-list">
          <div className="sa-student-list-header">
            <span>Student Candidate</span>
            <span>Email Address</span>
            <span>Allocated Stage(s)</span>
          </div>
          {filteredStudents.map((s, idx) => {
            const displayName = s.full_name || s.email?.split('@')[0] || 'Student';
            const avatarPalette = getAvatarColor(displayName);
            return (
              <div key={s.id} className={`sa-student-row ${idx % 2 === 0 ? 'even' : ''}`}>
                <div className="sa-student-name">
                  <div
                    className="sa-student-avatar"
                    style={{
                      background: avatarPalette.bg,
                      color: avatarPalette.text,
                      borderColor: avatarPalette.border
                    }}
                  >
                    {getInitials(displayName)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="sa-student-fullname">{displayName}</div>
                    <div className="sa-student-dept">
                      {s.department || s.branch || (s.usn ? `USN: ${s.usn}` : 'Enrolled Candidate')}
                    </div>
                  </div>
                </div>
                <div className="sa-student-email">
                  <code>{s.email}</code>
                </div>
                <div className="sa-student-stage">
                  {readOnly ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                      {(s.allocated_stages || (s.allocated_stage ? s.allocated_stage.split(',').map(x => x.trim()) : ['Basic'])).map(st => (
                        <StagePill key={st} stage={st} />
                      ))}
                    </div>
                  ) : (
                    <StageSelector
                      studentId={s.id}
                      currentStages={s.allocated_stages || (s.allocated_stage ? s.allocated_stage.split(',').map(x => x.trim()) : ['Basic'])}
                      onSave={handleSaveStage}
                      saving={savingId === s.id}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="stage-alloc-container">
      {view === 'batches' ? renderBatches() : renderDetail()}

      {internalToast && (
        <div className={`sa-toast sa-toast-${internalToast.type}`}>
          {internalToast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{internalToast.msg}</span>
          <button
            type="button"
            onClick={() => setInternalToast(null)}
            className="sa-toast-close"
            aria-label="Close notification"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default StageAllocation;
